import { useState, useMemo, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { CalendarIcon, Plus, X, Users, Gift, AlertCircle, AlertTriangle, ChevronLeft, ChevronRight, Check, UserPlus } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { validateEmail } from "@/lib/emailValidation";
import { InviteLinkSettings } from "@/components/InviteLinkSettings";

interface AddGroupGiftDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizerEmail: string;
  organizerName?: string;
  organizerPhone?: string;
  preselectedDate?: Date;
  onSuccess?: () => void;
}

interface InvitedPerson {
  id: string;
  email: string;
  phone: string;
  phoneCountryCode: string;
}

// Wizard steps definition
const STEPS = [
  { id: 1, name: "Basics", description: "What & when" },
  { id: 2, name: "Details", description: "Occasion & recipient" },
  { id: 3, name: "Invite", description: "Add friends" },
  { id: 4, name: "Review", description: "Confirm" },
];

// Animation variants for step transitions
const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 50 : -50,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 50 : -50,
    opacity: 0,
  }),
};

const OCCASIONS = [
  { value: "birthday", label: "🎂 Birthday" },
  { value: "anniversary", label: "💍 Anniversary" },
  { value: "wedding", label: "👰 Wedding" },
  { value: "festival", label: "🪔 Festival" },
  { value: "baby_shower", label: "👶 Baby Shower" },
  { value: "graduation", label: "🎓 Graduation" },
  { value: "housewarming", label: "🏠 Housewarming" },
  { value: "retirement", label: "🎉 Retirement" },
  { value: "promotion", label: "🚀 Promotion" },
  { value: "other", label: "✨ Other" },
];

const COUNTRY_CODES = [
  { code: "+91", flag: "🇮🇳" },
  { code: "+1", flag: "🇺🇸" },
  { code: "+44", flag: "🇬🇧" },
  { code: "+971", flag: "🇦🇪" },
];

export function AddGroupGiftDialog({
  open,
  onOpenChange,
  organizerEmail,
  organizerName,
  organizerPhone,
  preselectedDate,
  onSuccess,
}: AddGroupGiftDialogProps) {
  const navigate = useNavigate();
  
  // Wizard step state
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState(0);
  
  // Form fields
  const [occasion, setOccasion] = useState("");
  const [customOccasion, setCustomOccasion] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [message, setMessage] = useState("");
  const [date, setDate] = useState<Date | undefined>(preselectedDate);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [reminderDays, setReminderDays] = useState("7");
  const [isRecurring, setIsRecurring] = useState(true);
  const [sendEmailReminder, setSendEmailReminder] = useState(true);
  
  const [invitedPeople, setInvitedPeople] = useState<InvitedPerson[]>([]);
  const [newInviteEmail, setNewInviteEmail] = useState("");
  const [newInvitePhone, setNewInvitePhone] = useState("");
  const [newInvitePhoneCode, setNewInvitePhoneCode] = useState("+91");
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Validation state
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [emailError, setEmailError] = useState("");
  
  // Unsaved changes warning
  const [showExitWarning, setShowExitWarning] = useState(false);
  
  // Invite link settings
  const [inviteLinkEnabled, setInviteLinkEnabled] = useState(false);
  const [inviteLinkApprovalMode, setInviteLinkApprovalMode] = useState<"auto" | "manual">("auto");
  const [maxSelfInvites, setMaxSelfInvites] = useState<number | null>(null);

  // Check if form has unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return recipientName.trim() !== "" || 
           occasion !== "" ||
           invitedPeople.length > 0 ||
           date !== undefined;
  }, [recipientName, occasion, invitedPeople.length, date]);

  // Generate event name
  const generatedEventName = useMemo(() => {
    if (!recipientName.trim() || !occasion) return "";
    const occasionLabel = occasion === "other" 
      ? (customOccasion.trim() || "Event")
      : (OCCASIONS.find(o => o.value === occasion)?.label.replace(/^[^\s]+\s/, '') || occasion);
    return `${recipientName.trim()}'s ${occasionLabel}`;
  }, [recipientName, occasion, customOccasion]);

  // Real-time email validation
  const validateEmailInput = useCallback((email: string): string => {
    if (!email.trim()) return "";
    const result = validateEmail(email.trim());
    return result.valid ? "" : (result.error || "Invalid email");
  }, []);

  const handleEmailChange = (value: string) => {
    setNewInviteEmail(value);
    if (value.length > 3) {
      setEmailError(validateEmailInput(value));
    } else {
      setEmailError("");
    }
  };

  // Field validation
  const validateField = (field: string, value: string | Date | undefined): string => {
    switch (field) {
      case 'occasion':
        return value ? '' : 'Please select an occasion type';
      case 'recipientName':
        return (value as string)?.trim() ? '' : 'Please enter who this is for';
      case 'date':
        return value ? '' : 'Please select a date';
      default:
        return '';
    }
  };

  const handleFieldBlur = (field: string, value: string | Date | undefined) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, value);
    setFieldErrors(prev => ({ ...prev, [field]: error }));
  };

  // Inline error component
  const FieldError = ({ error }: { error?: string }) => {
    if (!error) return null;
    return (
      <div className="flex items-center gap-1.5 text-destructive mt-1.5">
        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="text-xs">{error}</span>
      </div>
    );
  };

  // Step validation
  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        const dateError = validateField('date', date);
        if (dateError) {
          setTouched(prev => ({ ...prev, date: true }));
          setFieldErrors(prev => ({ ...prev, date: dateError }));
          return false;
        }
        return true;
      case 2:
        const occasionError = validateField('occasion', occasion);
        const recipientError = validateField('recipientName', recipientName);
        if (occasionError || recipientError) {
          setTouched(prev => ({ ...prev, occasion: true, recipientName: true }));
          setFieldErrors(prev => ({ ...prev, occasion: occasionError, recipientName: recipientError }));
          return false;
        }
        return true;
      case 3:
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setDirection(1);
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
    }
  };

  const handleBack = () => {
    setDirection(-1);
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const resetForm = () => {
    setCurrentStep(1);
    setTouched({});
    setFieldErrors({});
    setOccasion("");
    setCustomOccasion("");
    setRecipientName("");
    setMessage("");
    setDate(undefined);
    setReminderDays("7");
    setIsRecurring(true);
    setSendEmailReminder(true);
    setInvitedPeople([]);
    setNewInviteEmail("");
    setNewInvitePhone("");
    setEmailError("");
    setInviteLinkEnabled(false);
    setInviteLinkApprovalMode("auto");
    setMaxSelfInvites(null);
  };

  const attemptDialogClose = () => {
    if (hasUnsavedChanges) {
      setShowExitWarning(true);
    } else {
      onOpenChange(false);
      resetForm();
    }
  };

  const handleAddPerson = () => {
    if (!newInviteEmail.trim()) {
      setEmailError("Email is required");
      return;
    }
    
    const emailValidation = validateEmail(newInviteEmail.trim());
    if (!emailValidation.valid) {
      setEmailError(emailValidation.error || "Invalid email address");
      return;
    }

    if (invitedPeople.some(p => p.email.toLowerCase() === newInviteEmail.toLowerCase().trim())) {
      setEmailError("This email is already added");
      return;
    }

    if (newInviteEmail.toLowerCase().trim() === organizerEmail.toLowerCase()) {
      setEmailError("You can't invite yourself");
      return;
    }

    setInvitedPeople([...invitedPeople, { 
      id: crypto.randomUUID(),
      email: newInviteEmail.trim(), 
      phone: newInvitePhone.trim(),
      phoneCountryCode: newInvitePhoneCode,
    }]);
    setNewInviteEmail("");
    setNewInvitePhone("");
    setEmailError("");
  };

  const handleRemovePerson = (id: string) => {
    setInvitedPeople(invitedPeople.filter(p => p.id !== id));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Final validation before submit
      if (!generatedEventName || !date) {
        toast.error("Please fill in all required fields");
        setIsSubmitting(false);
        return;
      }

      const firstInvitee = invitedPeople[0];
      const invitedFriendPhone = firstInvitee?.phone 
        ? `${firstInvitee.phoneCountryCode}${firstInvitee.phone.replace(/^0+/, '')}` 
        : null;

      const reminderData = {
        customer_email: organizerEmail?.toLowerCase() || null,
        customer_phone: organizerPhone || null,
        event_name: generatedEventName,
        recipient_name: recipientName.trim() || null,
        event_date: format(date!, "yyyy-MM-dd"),
        message: message.trim() || null,
        reminder_days_before: parseInt(reminderDays),
        is_recurring: isRecurring,
        reminder_email: sendEmailReminder ? (organizerEmail?.toLowerCase() || null) : null,
        occasion: occasion === "other" ? (customOccasion.trim() || "other") : (occasion || null),
        invited_friend_email: firstInvitee?.email || null,
        invited_friend_phone: invitedFriendPhone,
        invite_link_enabled: inviteLinkEnabled,
        invite_link_approval_mode: inviteLinkApprovalMode,
        max_self_invites: maxSelfInvites,
      };

      const { data, error } = await supabase
        .from("gift_reminders")
        .insert(reminderData)
        .select('id')
        .single();

      if (error) throw error;
      const reminderId = data?.id;

      // Add additional invitees to event_invitees table
      if (reminderId && invitedPeople.length > 1) {
        const additionalInvitees = invitedPeople.slice(1).map(person => ({
          reminder_id: reminderId,
          email: person.email.toLowerCase(),
          phone: person.phone ? `${person.phoneCountryCode}${person.phone.replace(/^0+/, '')}` : null,
          invited_by: organizerEmail?.toLowerCase() || 'organizer',
        }));
        
        await supabase
          .from("event_invitees")
          .insert(additionalInvitees);
      }

      // Send invitation emails
      if (reminderId && invitedPeople.length > 0) {
        try {
          await supabase.functions.invoke("send-event-invitation", {
            body: {
              reminderId,
              eventName: generatedEventName,
              organizerName: organizerName || organizerEmail?.split('@')[0] || 'Someone',
              organizerEmail,
              participants: invitedPeople.map(p => ({
                email: p.email,
                phone: p.phone ? `${p.phoneCountryCode}${p.phone}` : null,
              })),
              eventDate: format(date!, "yyyy-MM-dd"),
            },
          });
        } catch (emailError) {
          console.error("Error sending invitation emails:", emailError);
        }
      }

      toast.success("Group gift created! Redirecting to discussion...");
      resetForm();
      onOpenChange(false);
      onSuccess?.();
      navigate(`/events/${reminderId}`);
    } catch (error: any) {
      console.error("Error creating group gift:", error);
      const errorMessage = error?.message || error?.details || "Failed to create group gift";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={attemptDialogClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-customer" />
            Start a Group Gift
          </DialogTitle>
        </DialogHeader>

        {/* Progress Indicator */}
        <div className="py-4">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors",
                      currentStep > step.id
                        ? "bg-customer text-customer-foreground"
                        : currentStep === step.id
                        ? "bg-customer text-customer-foreground"
                        : "bg-muted text-muted-foreground"
                    )}
                  >
                    {currentStep > step.id ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      step.id
                    )}
                  </div>
                  <span className={cn(
                    "text-xs mt-1 hidden sm:block",
                    currentStep >= step.id ? "text-foreground" : "text-muted-foreground"
                  )}>
                    {step.name}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={cn(
                      "w-8 sm:w-12 h-0.5 mx-1",
                      currentStep > step.id ? "bg-customer" : "bg-muted"
                    )}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content with Animation */}
        <div className="relative overflow-hidden min-h-[280px]">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentStep}
              custom={direction}
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 },
              }}
              className="space-y-5 py-2"
            >
              {/* Step 1: Basics */}
              {currentStep === 1 && (
                <>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      Event Date
                      <span className="text-destructive">*</span>
                    </Label>
                    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !date && "text-muted-foreground",
                            touched.date && fieldErrors.date && "border-destructive"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? format(date, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={(d) => {
                            setDate(d);
                            setCalendarOpen(false);
                            if (touched.date) {
                              setFieldErrors(prev => ({ ...prev, date: d ? '' : 'Please select a date' }));
                            }
                          }}
                          initialFocus
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                    <FieldError error={touched.date ? fieldErrors.date : undefined} />
                  </div>

                  <div className="space-y-2">
                    <Label>Reminder Timing</Label>
                    <Select value={reminderDays} onValueChange={setReminderDays}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select timing" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3">3 days before</SelectItem>
                        <SelectItem value="7">7 days before</SelectItem>
                        <SelectItem value="14">14 days before</SelectItem>
                        <SelectItem value="30">30 days before</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between">
                    <Label htmlFor="recurring" className="font-normal">Repeat every year</Label>
                    <Checkbox
                      id="recurring"
                      checked={isRecurring}
                      onCheckedChange={(checked) => setIsRecurring(checked === true)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label htmlFor="email-reminder" className="font-normal">Send email reminder</Label>
                    <Checkbox
                      id="email-reminder"
                      checked={sendEmailReminder}
                      onCheckedChange={(checked) => setSendEmailReminder(checked === true)}
                    />
                  </div>
                </>
              )}

              {/* Step 2: Details */}
              {currentStep === 2 && (
                <>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      Occasion Type
                      <span className="text-destructive">*</span>
                    </Label>
                    <Select 
                      value={occasion} 
                      onValueChange={(val) => { 
                        setOccasion(val); 
                        if (val !== "other") setCustomOccasion(""); 
                        if (touched.occasion) {
                          setFieldErrors(prev => ({ ...prev, occasion: val ? '' : 'Please select an occasion type' }));
                        }
                      }}
                    >
                      <SelectTrigger className={cn(touched.occasion && fieldErrors.occasion && "border-destructive")}>
                        <SelectValue placeholder="Select occasion type" />
                      </SelectTrigger>
                      <SelectContent>
                        {OCCASIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FieldError error={touched.occasion ? fieldErrors.occasion : undefined} />
                    {occasion === "other" && (
                      <Input
                        placeholder="Enter occasion name"
                        value={customOccasion}
                        onChange={(e) => setCustomOccasion(e.target.value)}
                        className="mt-2"
                      />
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="recipientName" className="flex items-center gap-1">
                      Who is it for?
                      <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="recipientName"
                      placeholder="e.g., John, Mom & Dad"
                      value={recipientName}
                      onChange={(e) => {
                        setRecipientName(e.target.value);
                        if (touched.recipientName) {
                          setFieldErrors(prev => ({ ...prev, recipientName: e.target.value.trim() ? '' : 'Please enter who this is for' }));
                        }
                      }}
                      onBlur={() => handleFieldBlur('recipientName', recipientName)}
                      className={cn(touched.recipientName && fieldErrors.recipientName && "border-destructive")}
                    />
                    <FieldError error={touched.recipientName ? fieldErrors.recipientName : undefined} />
                  </div>

                  {generatedEventName && (
                    <div className="p-3 rounded-lg bg-customer/10 border border-customer/20">
                      <p className="text-sm font-medium text-customer">
                        📝 {generatedEventName}
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="message">Message (optional)</Label>
                    <Textarea
                      id="message"
                      placeholder="Add a personal note..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={2}
                    />
                  </div>
                </>
              )}

              {/* Step 3: Invite */}
              {currentStep === 3 && (
                <>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-customer" />
                      <Label className="text-sm font-medium">Invite Friends to Contribute</Label>
                    </div>

                    {invitedPeople.length > 0 && (
                      <div className="flex flex-wrap gap-2 p-2 bg-muted rounded-md border">
                        {invitedPeople.map((person) => (
                          <div key={person.id} className="flex items-center gap-1 bg-customer/15 text-customer px-2.5 py-1 rounded-full text-xs font-medium">
                            <span>{person.email}</span>
                            <button
                              type="button"
                              onClick={() => handleRemovePerson(person.id)}
                              className="ml-1 hover:text-destructive transition-colors"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          type="email"
                          placeholder="Friend's email"
                          value={newInviteEmail}
                          onChange={(e) => handleEmailChange(e.target.value)}
                          className={cn("flex-1", emailError && "border-destructive")}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleAddPerson}
                          disabled={!newInviteEmail.trim()}
                          className="h-10 px-3 gap-1.5"
                        >
                          <UserPlus className="h-4 w-4" />
                          Add
                        </Button>
                      </div>
                      <FieldError error={emailError} />
                      <div className="flex gap-2">
                        <Select value={newInvitePhoneCode} onValueChange={setNewInvitePhoneCode}>
                          <SelectTrigger className="w-20 h-10">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {COUNTRY_CODES.map((c) => (
                              <SelectItem key={c.code} value={c.code}>
                                {c.flag} {c.code}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          type="tel"
                          placeholder="Phone (optional)"
                          value={newInvitePhone}
                          onChange={(e) => setNewInvitePhone(e.target.value.replace(/\D/g, ''))}
                          className="flex-1"
                        />
                      </div>
                    </div>

                    <InviteLinkSettings
                      enabled={inviteLinkEnabled}
                      onEnabledChange={setInviteLinkEnabled}
                      approvalMode={inviteLinkApprovalMode}
                      onApprovalModeChange={setInviteLinkApprovalMode}
                      maxSelfInvites={maxSelfInvites}
                      onMaxSelfInvitesChange={setMaxSelfInvites}
                    />
                  </div>
                </>
              )}

              {/* Step 4: Review */}
              {currentStep === 4 && (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg border bg-muted/30">
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <Gift className="w-5 h-5 text-customer" />
                      {generatedEventName || "Group Gift"}
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Date</span>
                        <span>{date ? format(date, "PPP") : "Not set"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Recipient</span>
                        <span>{recipientName || "Not set"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Occasion</span>
                        <Badge variant="secondary">
                          {OCCASIONS.find(o => o.value === occasion)?.label || customOccasion || "Not set"}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Participants</span>
                        <span>{invitedPeople.length + 1} (incl. you)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Recurring</span>
                        <span>{isRecurring ? "Yes, yearly" : "One-time"}</span>
                      </div>
                      {inviteLinkEnabled && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Invite Link</span>
                          <Badge variant="outline" className="text-xs">Enabled</Badge>
                        </div>
                      )}
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground text-center">
                    You can configure payment collection in the discussion page
                  </p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={currentStep === 1 ? attemptDialogClose : handleBack}
            disabled={isSubmitting}
          >
            {currentStep === 1 ? "Cancel" : (
              <>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </>
            )}
          </Button>

          {currentStep < STEPS.length ? (
            <Button onClick={handleNext} className="bg-customer hover:bg-customer/90 text-customer-foreground">
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-customer hover:bg-customer/90 text-customer-foreground"
            >
              {isSubmitting ? "Creating..." : "Create Group Gift"}
            </Button>
          )}
        </div>
      </DialogContent>

      {/* Unsaved Changes Warning Dialog */}
      <AlertDialog open={showExitWarning} onOpenChange={setShowExitWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="mx-auto w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-2">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
            </div>
            <AlertDialogTitle className="text-center">Discard Changes?</AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              You have unsaved changes. Are you sure you want to close?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:justify-center gap-2">
            <AlertDialogCancel>Keep Editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowExitWarning(false);
                onOpenChange(false);
                resetForm();
              }}
              className="bg-destructive hover:bg-destructive/90"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
